import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Avatar,
    Box,
    Chip,
    CircularProgress,
    ClickAwayListener,
    IconButton,
    List,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Paper,
    Popper,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import ReplyIcon from '@mui/icons-material/Reply';
import SendIcon from '@mui/icons-material/Send';
import { AlertInfo } from "../Alerts/Alert";
import { apiUrl } from "../../services/api";
import {
    ChatMensagemType,
    ChatParticipante,
    corAvatar,
    extrairMencoes,
    iniciaisNome,
    mencaoEmDigitacao,
    normalizar,
    previewMensagemCurta,
    tamanhoLegivel,
} from "./chatTypes";

type Props = {
    enviando: boolean
    participantes?: ChatParticipante[]   // vazio em conversa direta = menção desligada
    idUsuarioLogado?: number
    respondendo?: ChatMensagemType | null   // mensagem citada, mostrada acima do campo
    onCancelarResposta?: () => void
    onEnviarTexto: (texto: string, mencoes: number[]) => void
    onEnviarAnexos: (arquivos: File[], texto: string, mencoes: number[]) => void
};

const TAMANHO_MAXIMO = 10 * 1024 * 1024; // igual ao limite do multerChat no backend
const MAXIMO_ARQUIVOS = 5;
const MAXIMO_SUGESTOES = 6;

export default function ChatInput({ enviando, participantes = [], idUsuarioLogado = 0, respondendo = null, onCancelarResposta, onEnviarTexto, onEnviarAnexos }: Props) {
    const [texto, setTexto] = useState("");
    const [arquivos, setArquivos] = useState<File[]>([]);
    const inputFileRef = useRef<HTMLInputElement | null>(null);
    const campoRef = useRef<HTMLTextAreaElement | null>(null);
    const caixaRef = useRef<HTMLDivElement | null>(null);

    // Escolher "Responder" já deixa o cursor no campo
    useEffect(() => {
        if (respondendo) campoRef.current?.focus();
    }, [respondendo]);

    // Estado da menção em digitação: posição do "@" + termo depois dele
    const [mencao, setMencao] = useState<{ inicio: number, termo: string } | null>(null);
    const [indiceSugestao, setIndiceSugestao] = useState(0);

    // Só faz sentido mencionar quem ainda está no grupo, fora eu mesmo
    const mencionaveis = useMemo(
        () => participantes.filter(p => Number(p.id_usuario) !== Number(idUsuarioLogado)),
        [participantes, idUsuarioLogado],
    );

    const sugestoes = useMemo(() => {
        if (!mencao) return [];
        const termo = normalizar(mencao.termo);
        return mencionaveis
            .filter(p => termo === "" || normalizar(p.nome_usuario).includes(termo))
            .slice(0, MAXIMO_SUGESTOES);
    }, [mencao, mencionaveis]);

    const sugerindo = mencao !== null && sugestoes.length > 0;

    function fecharSugestoes() {
        setMencao(null);
        setIndiceSugestao(0);
    }

    function atualizarMencao(valor: string, cursor: number) {
        if (mencionaveis.length === 0) return fecharSugestoes();
        const atual = mencaoEmDigitacao(valor, cursor);
        setMencao(atual);
        setIndiceSugestao(0);
    }

    function handleChangeTexto(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const valor = e.target.value;
        setTexto(valor);
        atualizarMencao(valor, e.target.selectionStart ?? valor.length);
    }

    // Troca o "@termo" pelo nome completo e devolve o cursor logo depois dele
    function escolherMencao(participante: ChatParticipante) {
        if (!mencao) return;
        const antes = texto.substring(0, mencao.inicio);
        const depois = texto.substring(mencao.inicio + 1 + mencao.termo.length);
        const inserido = `@${participante.nome_usuario} `;
        const novo = `${antes}${inserido}${depois}`;
        const cursor = antes.length + inserido.length;

        setTexto(novo);
        fecharSugestoes();

        // o valor só existe no DOM depois do render
        setTimeout(() => {
            campoRef.current?.focus();
            campoRef.current?.setSelectionRange(cursor, cursor);
        }, 0);
    }

    function selecionarArquivos(e: React.ChangeEvent<HTMLInputElement>) {
        const lista = Array.from(e.target.files || []);
        e.target.value = ""; // permite escolher o mesmo arquivo de novo

        const grandes = lista.filter(f => f.size > TAMANHO_MAXIMO);
        if (grandes.length > 0) {
            AlertInfo({ icon: 'error', title: 'Arquivo muito grande', text: `Limite de 10MB por arquivo: ${grandes.map(f => f.name).join(', ')}` });
        }

        const validos = lista.filter(f => f.size <= TAMANHO_MAXIMO);
        setArquivos(prev => {
            const juntos = [...prev, ...validos];
            if (juntos.length > MAXIMO_ARQUIVOS) {
                AlertInfo({ icon: 'info', title: 'Máximo de 5 arquivos', text: 'Os arquivos excedentes foram descartados.' });
            }
            return juntos.slice(0, MAXIMO_ARQUIVOS);
        });
    }

    function handleEnviar() {
        if (enviando) return;

        const mencoes = extrairMencoes(texto, mencionaveis);
        fecharSugestoes();

        if (arquivos.length > 0) {
            onEnviarAnexos(arquivos, texto, mencoes);
            setArquivos([]);
            setTexto("");
            return;
        }
        if (String(texto).trim() === "") return;
        onEnviarTexto(texto, mencoes);
        setTexto("");
    }

    // Com a lista de menções aberta, as setas/Enter navegam nela; senão Enter envia
    function handleKeyDown(e: React.KeyboardEvent) {
        if (sugerindo) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setIndiceSugestao(i => (i + 1) % sugestoes.length);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setIndiceSugestao(i => (i - 1 + sugestoes.length) % sugestoes.length);
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                escolherMencao(sugestoes[indiceSugestao]);
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                fecharSugestoes();
                return;
            }
        }

        if (e.key === 'Escape' && respondendo && onCancelarResposta) {
            e.preventDefault();
            onCancelarResposta();
            return;
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleEnviar();
        }
    }

    return (
        <Box ref={caixaRef} sx={{ borderTop: '1px solid #e0e0e0', p: 1, bgcolor: '#fff' }}>
            {respondendo && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.8,
                        mb: 0.8,
                        px: 0.8,
                        py: 0.5,
                        borderRadius: 1,
                        borderLeft: `3px solid ${corAvatar(respondendo.id_usuario_remetente)}`,
                        bgcolor: '#f1f3f4',
                    }}
                >
                    <ReplyIcon sx={{ fontSize: 16, color: '#757575' }} />
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 'bold', color: corAvatar(respondendo.id_usuario_remetente) }}>
                            Respondendo a {Number(respondendo.id_usuario_remetente) === Number(idUsuarioLogado) ? 'você mesmo' : respondendo.nome_usuario}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#616161', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {previewMensagemCurta(respondendo)}
                        </Typography>
                    </Box>
                    <Tooltip title="Cancelar resposta">
                        <IconButton size="small" onClick={onCancelarResposta} sx={{ p: 0.3 }}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {arquivos.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.8 }}>
                    {arquivos.map((f, i) => (
                        <Chip
                            key={`${f.name}${i}`}
                            size="small"
                            label={`${f.name} (${tamanhoLegivel(f.size)})`}
                            onDelete={() => setArquivos(prev => prev.filter((_, idx) => idx !== i))}
                            sx={{ maxWidth: 220 }}
                        />
                    ))}
                </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
                <input
                    ref={inputFileRef}
                    type="file"
                    multiple
                    hidden
                    onChange={selecionarArquivos}
                />
                <Tooltip title="Anexar arquivo (máx. 10MB)">
                    <span>
                        <IconButton size="small" onClick={() => inputFileRef.current?.click()} disabled={enviando}>
                            <AttachFileIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>

                <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={4}
                    placeholder={mencionaveis.length > 0 ? "Escreva uma mensagem... (@ menciona alguém)" : "Escreva uma mensagem..."}
                    value={texto}
                    inputRef={campoRef}
                    onChange={handleChangeTexto}
                    onKeyDown={handleKeyDown}
                    onClick={e => atualizarMencao(texto, (e.target as HTMLTextAreaElement).selectionStart ?? texto.length)}
                    onBlur={fecharSugestoes}
                    InputProps={{ sx: { fontSize: 13, bgcolor: '#f5f5f5' } }}
                />

                <Tooltip title="Enviar">
                    <span>
                        <IconButton
                            color="primary"
                            onClick={handleEnviar}
                            disabled={enviando || (String(texto).trim() === "" && arquivos.length === 0)}
                        >
                            {enviando ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {/* Lista flutuante de menções: abre para CIMA, não empurra o layout */}
            <Popper
                open={sugerindo}
                anchorEl={caixaRef.current}
                placement="top-start"
                style={{ zIndex: 1400 }}
                modifiers={[{ name: 'offset', options: { offset: [8, 4] } }]}
            >
                <ClickAwayListener onClickAway={fecharSugestoes}>
                    <Paper elevation={4} sx={{ minWidth: 200, maxWidth: 300 }}>
                        <Box sx={{ px: 1.2, py: 0.5, borderBottom: '1px solid #eceff1' }}>
                            <Typography sx={{ fontSize: 10.5, color: '#757575' }}>
                                Mencionar participante
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0.3, maxHeight: 220, overflowY: 'auto' }}>
                            {sugestoes.map((p, i) => (
                                <ListItemButton
                                    key={p.id_usuario}
                                    selected={i === indiceSugestao}
                                    onMouseDown={e => e.preventDefault()} // impede o blur antes do clique
                                    onClick={() => escolherMencao(p)}
                                    sx={{ py: 0.3 }}
                                >
                                    <ListItemAvatar sx={{ minWidth: 34 }}>
                                        <Avatar
                                            src={p.photo_usuario ? `${apiUrl}/imguploaded/${p.photo_usuario}` : ""}
                                            sx={{ width: 26, height: 26, fontSize: 11, bgcolor: corAvatar(p.id_usuario) }}
                                        >
                                            {iniciaisNome(p.nome_usuario)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={p.nome_usuario}
                                        primaryTypographyProps={{ fontSize: 12, noWrap: true }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
}
